import { DatePipe } from '@angular/common';
import { Component, effect, inject, input, OnDestroy, untracked } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IonButton, IonCard, IonIcon, IonText } from '@ionic/angular/standalone';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { withProductReviewsList } from '@libs/mtjr/shared/generated/entities/product-review/product-review-store-features';
import { signalStore } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const ProductReviewsStore = signalStore(withProductReviewsList());

@Component({
  selector: 'mtjr-product-reviews-section',
  standalone: true,
  providers: [ProductReviewsStore],
  imports: [
    IonText,
    IonButton,
    IonCard,
    IonIcon,
    VxLoadingComponent,
    VxErrorComponent,
    DatePipe,
    RouterLink,
    TranslatePipe,
  ],
  template: `
    @switch (store.productReviewsListState().type) {
      @case ('loading') {
        <mtjr-loading />
      }
      @case ('error') {
        <mtjr-error [failure]="store.productReviewsFailure()" />
      }
      @case ('success') {
        @if (store.productReviews().length > 0) {
          <div class="flex items-center">
            <ion-text class="ms-6 flex-1">{{ 'PRODUCTS.TOP_REVIEWS' | translate }}</ion-text>

            <ion-button fill="clear" color="primary" routerLink="reviews" class="text-primary capitalize">
              {{ 'COMMON.SHOW_ALL' | translate }}
            </ion-button>
          </div>
        }
        <div class="flex gap-2 overflow-x-auto pe-4 ps-5" style="scrollbar-width: none; -ms-overflow-style: none">
          @for (review of store.productReviews(); track $index) {
            <ion-card class="!m-0 min-w-[250px] px-4 py-3">
              <div class="flex h-full flex-col">
                <div class="flex items-center">
                  <ion-text class="text-primary flex-1">{{ review.userName }}</ion-text>

                  <div class="flex" slot="end">
                    @for (i of [1, 2, 3, 4, 5]; track $index) {
                      @if (review.rating >= i) {
                        <ion-icon slot="icon-only" class="me-1 text-sm" color="warning" name="star-filled" />
                      } @else {
                        <ion-icon slot="icon-only" class="me-1 text-sm" color="warning" name="star" />
                      }
                    }
                  </div>
                </div>

                <ion-text color="medium" class="mt-2 flex-1">{{ review.review }}</ion-text>

                <div class="bg-light mb-1 mt-2 h-[1px] w-full"></div>
                <div class="flex">
                  <span class="text-medium ms-1 text-xs">{{ review.createdAt | date }}</span>
                </div>
              </div>
            </ion-card>
          }
        </div>
      }
    }
  `,
})
export class VxProductReviewsSectionComponent implements OnDestroy {
  store = inject(ProductReviewsStore);
  eventBus = inject(NgEventBus);

  productId = input<string | undefined>();

  private reviewsSubscription?: Subscription;

  constructor() {
    effect(() => {
      const productId = this.productId();
      if (productId) {
        untracked(() => {
          this.store.productReviewsListPage({ productId }, { totalPerPage: 6 });
        });
      }
    });

    this.reviewsSubscription = this.eventBus.on('reviews:fetch').subscribe(() => {
      const productId = this.productId();
      if (productId) {
        this.store.productReviewsListPage({ productId });
      }
    });
  }

  ngOnDestroy() {
    this.reviewsSubscription?.unsubscribe();
  }
}
